import React, { useState, useEffect } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../hooks/useTranslation';
import { getUsageStats } from '../services/usageService';

const SearchLimitWarning = ({ usageStats: propUsageStats, loading: propLoading }) => {
  const { currentOrg } = useAuth();
  const t = useTranslation();
  const [internalUsageStats, setInternalUsageStats] = useState(null);
  const [internalLoading, setInternalLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);

  // Use prop usageStats if provided, otherwise load internally
  const usageStats = propUsageStats || internalUsageStats;
  const loading = propLoading !== undefined ? propLoading : internalLoading;

  useEffect(() => {
    // Only load internally if no props provided
    if (propUsageStats === undefined) {
      const loadUsageStats = async () => {
        if (!currentOrg?.id) return;
        
        try {
          setInternalLoading(true);
          const stats = await getUsageStats(currentOrg.id);
          setInternalUsageStats(stats);
        } catch (error) {
          console.error('Error loading usage stats for warning:', error);
        } finally {
          setInternalLoading(false);
        }
      };

      loadUsageStats();
    }
  }, [currentOrg?.id, propUsageStats]);

  // Calculate these values before any early returns
  const isAtLimit = usageStats ? !usageStats.allowed : false;
  const isNearLimit = usageStats ? (usageStats.isNearLimit && usageStats.allowed) : false;
  
  // Reset dismissed state when limit is reached
  useEffect(() => {
    if (isAtLimit && dismissed) {
      setDismissed(false);
    }
  }, [isAtLimit, dismissed]);

  // Don't show warning if loading or not approaching limit
  if (loading || !usageStats || (!usageStats.isNearLimit && usageStats.allowed)) {
    return null;
  }
  
  // Don't show if dismissed and it's only a near limit warning (not at limit)
  if (dismissed && isNearLimit && !isAtLimit) {
    return null;
  }

  return (
    <div className={`rounded-lg p-4 mb-4 ${
      isAtLimit 
        ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700'
        : 'bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700'
    }`}>
      <div className="flex items-start">
        <AlertTriangle className={`w-5 h-5 mt-0.5 mr-3 ${
          isAtLimit ? 'text-red-500' : 'text-yellow-500'
        }`} />
        <div className="flex-1">
          {/* Only show dismiss button for near limit warning, not at limit */}
          {isNearLimit && !isAtLimit && (
            <button
              onClick={() => setDismissed(true)}
              className="float-right text-yellow-400 hover:text-yellow-600 dark:text-yellow-500 dark:hover:text-yellow-300"
            >
              <X className="w-4 h-4" />
            </button>
          )}
          <h3 className={`font-medium ${
            isAtLimit 
              ? 'text-red-800 dark:text-red-300' 
              : 'text-yellow-800 dark:text-yellow-300'
          }`}>
            {isAtLimit ? 'Search Limit Reached' : 'Approaching Search Limit'}
          </h3>
          <p className={`text-sm mt-1 ${
            isAtLimit 
              ? 'text-red-700 dark:text-red-400' 
              : 'text-yellow-700 dark:text-yellow-400'
          }`}>
            {isAtLimit ? (
              `You have used all ${usageStats.totalLimit} searches for this month. Upgrade your plan to continue searching.`
            ) : (
              `You have used ${usageStats.used} of ${usageStats.totalLimit} searches this month (${usageStats.remaining} remaining).`
            )}
          </p>
          {usageStats.additionalUsage > 0 && (
            <p className={`text-xs mt-1 ${
              isAtLimit 
                ? 'text-red-600 dark:text-red-400' 
                : 'text-yellow-600 dark:text-yellow-400'
            }`}>
              Including {usageStats.additionalUsage} bonus searches
            </p>
          )}
          
        </div>
      </div>
    </div>
  );
};

export default SearchLimitWarning; 